<template>
	<main>
		<section class="w-full bg-white-dark-1 md:py-8">
			<Container class="!pb-0">
				<UnavailablePropertyState
					:title="t('unavailablePropertyTitle')"
					:description="t('unavailablePropertyDescription')"
					title-classes="text-xl md:text-2xl"
					description-classes="text-sm md:text-lg"
					icon-classes="cn('size-7 text-white')"
					home-url="/"
					:go-to-home-text="t('goToHome')"
					:property-url="getPropertiesUrl"
					:see-properties-text="seePropertiesText"
					:on-go-to-home-click="handleClickGoToHome"
					:on-view-more-click="handleViewMoreClickOnMessageDisplay"
				></UnavailablePropertyState>
			</Container>
		</section>

		<section
			v-if="property && similarProperties && similarProperties.length"
			class="border-b border-white-dark-3 bg-white md:px-20"
		>
			<Container class="pb-0 md:!pb-10">
				<HighlightedRegionPropertiesDetailsSection
					:properties="similarProperties"
					:region="property.region"
					:property-type="property.propertyType.identifier"
					:city="property.city"
					:state="property.state"
					:title="`Imóveis semelhantes em ${property.region}`"
					button-text="Ver todos os imóveis semelhantes"
					container-class="!px-0 !py-8 md:!py-2"
					button-variant="secondary"
					button-class="border-none hover:underline underline-offset-4 decoration-white-dark-3 !px-0"
					carousel-item-class="sm:basis-[32%]"
					carousel-class="w-full !gap-0"
					title-class="text-2xl md:text-[28px]"
					@cta-view-more-clicked="handleViewMoreClickedSimilarProperties"
				/>
			</Container>
		</section>

		<section
			v-if="chosenForYouProperties && chosenForYouProperties.length >= 5"
			class="bg-white md:!px-20"
		>
			<Container class="pt-0 md:!pt-8">
				<ChosenPropertiesSection
					:properties="chosenForYouProperties"
					:cta-link="chosenForYouCtaLink"
					class="!p-0 md:!py-0"
					:is-loading="isLoadingSelectedProperties"
					container-class="!px-0 !pt-14 md:!pt-10"
					title-class="text-2xl tracking-wide md:tracking-tight md:text-[28px]"
					@cta_view_more_clicked="handleViewMoreClickedSelectedProperties"
				/>
			</Container>
		</section>
	</main>
</template>

<script setup lang="ts">
import type { Property } from '~/core/entities/property';
import type { SectionName } from '~/types/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import HighlightedRegionPropertiesDetailsSection from '~/components/Sections/HighlightedRegionPropertiesDetailsSection/HighlightedRegionPropertiesDetailsSection.vue';
import ChosenPropertiesSection from '~/components/Sections/ChosenPropertiesSection/ChosenPropertiesSection.vue';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { useI18n } from 'vue-i18n';
import UnavailablePropertyState from '~/components/UnavailableProperty/UnavailablePropertyState.vue';

const { unavailableCommercialId: routeCommercialId } = useRoute().params as {
	unavailableCommercialId: string;
};

const isLoadingSelectedProperties = computed(() => {
	return selectedPropertiesRequestStatus.value === 'pending';
});

const { data: property } = await useAsyncData<Property | null>(
	`imovel:${routeCommercialId}`,
	async () => {
		const [property, error] =
			await nuxtPropertiesRepository.getOne(routeCommercialId);

		if (!property || error) {
			navigateTo({
				path: '/error',
				query: {
					commercialId: routeCommercialId
				}
			});
		}

		return property;
	}
);

const { data: similarProperties } = await useAsyncData<Property[]>(async () => {
	if (!property.value) return [];

	const query = {
		identifier: property.value.commercialId,
		region: property.value.region,
		propertyType: [property.value.propertyType.identifier]
	};

	const [result, error] =
		await nuxtPropertiesRepository.getSimilarProperties(query);

	if (error) return [];
	return result;
});

const chosenForYouCtaLink = computed<string>(() => {
	if (!property.value) return '';

	return PropertyFiltersPresenter.getPathFromFilters({
		regions: [propertyRegion.value],
		states: [property.value.state ?? ''],
		cities: [property.value.city ?? ''],
		maxAskingPrice: Math.ceil((property.value.askingPrice ?? 0) * 1.15),
		isChosenForYou: true
	});
});

const {
	data: chosenForYouProperties,
	status: selectedPropertiesRequestStatus
} = useAsyncData<Property[] | null>(
	`imovel:${routeCommercialId}:chosen-for-you`,
	async () => {
		if (!property.value) return null;
		const [chosenForYouProperties, error] =
			await nuxtPropertiesRepository.getChosenForYouProperties({
				identifier: routeCommercialId,
				regions: [property.value?.region || ''],
				askingPrice: property.value.askingPrice ?? undefined
			});

		if (!chosenForYouProperties || error) return null;

		return chosenForYouProperties;
	}
);

const getPropertiesUrl = computed(() => {
	return PropertyFiltersPresenter.getPathFromFilters({
		regions: [propertyRegion.value],
		cities: [property.value?.city || ''],
		states: [property.value?.state || '']
	});
});

const { t } = useI18n({
	useScope: 'local',
	messages: {
		pt: {
			goToHome: 'Ir para Home',
			seeProperties: 'Ver imóveis em {region}',
			unavailablePropertyTitle: 'Este imóvel não está mais disponível',
			unavailablePropertyDescription:
				'Explore outras opções de imóveis semelhantes na mesma região.'
		}
	}
});

const {
	trackPageView,
	trackCtaOffMarketClicked,
	trackCtaViewMoreClicked,
	trackCtaHomeClicked,
	trackPageError
} = useEvents();

const propertyRegion = computed(() => property.value?.region || '');
const currentPageEventName = 'Unpublished Property';
const seePropertiesText = computed(() => {
	return t('seeProperties', { region: propertyRegion.value });
});

const route = useRoute();
onMounted(() => {
	trackPageView({
		current_page: currentPageEventName,
		previous_page_url: `/imovel/${routeCommercialId}`,
		event_name: 'page_viewed'
	});

	trackPageError({
		[MixpanelProps.ERROR_CODE]: 'unpublished property',
		[MixpanelProps.PAGE_URL]: route.fullPath,
		[MixpanelProps.COMMERCIAL_ID]: routeCommercialId
	});
});

useEventBus<void>('cta_off_market_clicked').on(() => {
	trackCtaOffMarketClicked({
		[MixpanelProps.CTA_TEXT]: 'Imóveis Off-Market',
		[MixpanelProps.CURRENT_PAGE]: currentPageEventName,
		[MixpanelProps.SECTION_NAME]: 'selected properties',
		[MixpanelProps.DESTINATION_URL]: '/off-market'
	});
});

const handleViewMoreClickedSimilarProperties = (event: {
	destination: string;
	clickCount: number;
	ctaText: string;
}) => {
	const { destination, clickCount, ctaText } = event;
	handleViewMoreClicked({
		destination,
		clickCount,
		ctaText,
		sectionName: 'similar properties'
	});
};

const handleViewMoreClickedSelectedProperties = (event: {
	href: string;
	clickCount: number;
	ctaText: string;
}) => {
	const { href, clickCount, ctaText } = event;
	handleViewMoreClicked({
		destination: href,
		clickCount,
		ctaText,
		sectionName: 'selected properties'
	});
};

const handleViewMoreClicked = ({
	destination,
	clickCount,
	ctaText,
	sectionName
}: {
	destination: string;
	clickCount: number;
	ctaText: string;
	sectionName: SectionName;
}) => {
	trackCtaViewMoreClicked({
		cta_text: ctaText,
		destination_url: destination,
		click_count: clickCount,
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.CURRENT_PAGE]: currentPageEventName
	});
};

const handleViewMoreClickOnMessageDisplay = () => {
	handleViewMoreClicked({
		destination: getPropertiesUrl.value,
		clickCount: 1,
		ctaText: seePropertiesText.value,
		sectionName: 'property unavailable'
	});
};

const handleClickGoToHome = () => {
	trackCtaHomeClicked({
		cta_text: t('goToHome'),
		destination_url: '/',
		[MixpanelProps.CURRENT_PAGE]: currentPageEventName
	});
};
</script>
